import React, { useState, useEffect, useRef } from "react";
import viewEyeIcon from "../../assets/eye.svg";
import { useNavigate } from "react-router-dom";
import nodata from "../../assets/nodata.svg";
import { Modal } from "antd";
import { Calendar, User, Stethoscope, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const CalendarDatePicker = ({ dateRange, setDateRange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingMode, setSelectingMode] = useState("from"); // 'from' or 'until'
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDisplayDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getDisplayText = () => {
    const [start, end] = dateRange;
    if (!start && !end) return "Select date range";
    if (start && !end) return `From ${formatDisplayDate(start)}`;
    if (start && end) {
      if (start.toDateString() === end.toDateString()) {
        return formatDisplayDate(start);
      }
      return `${formatDisplayDate(start)} - ${formatDisplayDate(end)}`;
    }
    return "Select date range";
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getPreviousMonthDays = (date) => {
    const prevMonth = new Date(date.getFullYear(), date.getMonth() - 1, 0);
    return prevMonth.getDate();
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.toDateString() === date2.toDateString();
  };

  const isInRange = (date) => {
    const [start, end] = dateRange;
    if (!start || !end) return false;
    return date >= start && date <= end;
  };

  const isRangeStart = (date) => {
    return isSameDay(date, dateRange[0]);
  };

  const isRangeEnd = (date) => {
    return isSameDay(date, dateRange[1]);
  };

  const handleDateClick = (date) => {
    const [start, end] = dateRange;

    if (!start || (start && end)) {
      // Start new selection
      setDateRange([date, null]);
      setSelectingMode("until");
    } else if (start && !end) {
      // Complete the range
      if (date >= start) {
        setDateRange([start, date]);
        setSelectingMode("from");
        setIsOpen(false);
      } else {
        setDateRange([date, start]);
        setSelectingMode("from");
        setIsOpen(false);
      }
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDayOfMonth = getFirstDayOfMonth(currentMonth);
    const prevMonthDays = getPreviousMonthDays(currentMonth);

    const days = [];
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // Previous month days (grayed out)
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() - 1,
        day
      );
      days.push(
        <button
          key={`prev-${day}`}
          onClick={() => handleDateClick(date)}
          className="w-10 h-10 text-gray-300 hover:bg-gray-100 rounded-full text-sm"
        >
          {day}
        </button>
      );
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        day
      );
      const isStart = isRangeStart(date);
      const isEnd = isRangeEnd(date);
      const inRange = isInRange(date);
      const isToday = isSameDay(date, new Date());

      let className =
        "w-10 h-10 text-gray-700 hover:bg-green-100 rounded-full text-sm relative ";

      if (isStart || isEnd) {
        className += "bg-green-400 text-white hover:bg-green-500 ";
      } else if (inRange) {
        className += "bg-green-200 text-green-800 ";
      } else if (isToday) {
        className += "border border-green-600 ";
      }

      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(date)}
          className={className}
        >
          {day}
        </button>
      );
    }

    // Next month days (grayed out)
    const totalCells = 42; // 6 rows × 7 days
    const remainingCells = totalCells - days.length;
    for (let day = 1; day <= remainingCells; day++) {
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + 1,
        day
      );
      days.push(
        <button
          key={`next-${day}`}
          onClick={() => handleDateClick(date)}
          className="w-10 h-10 text-gray-300 hover:bg-gray-100 rounded-full text-sm"
        >
          {day}
        </button>
      );
    }

    return (
      <div className="p-4">
        {/* Header with month/year and navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-lg font-medium">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </div>
          <button
            onClick={() => navigateMonth(1)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* From/Until tabs */}
        <div className="flex mb-4 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setSelectingMode("from")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              selectingMode === "from"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            From
          </button>
          <button
            onClick={() => setSelectingMode("until")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              selectingMode === "until"
                ? "bg-green-400 text-white shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Until
          </button>
        </div>

        {/* Day names */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map((day) => (
            <div
              key={day}
              className="w-10 h-8 flex items-center justify-center text-xs font-medium text-gray-500"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">{days}</div>
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors min-w-[200px] text-left"
      >
        <Calendar className="w-4 h-4 text-gray-500" />
        <span className="flex-1 text-sm text-gray-700">{getDisplayText()}</span>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>

      {/* Calendar Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] w-80">
          {renderCalendar()}
        </div>
      )}
    </div>
  );
};

export default function FollowUpPatients() {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [followUpPatients, setFollowUpPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  // const [dateRange, setDateRange] = useState([null, null]);
  const [dateRange, setDateRange] = useState([
  new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
  new Date() // today
]);

  // Fetch follow up patients from API
  const fetchFollowUpPatients = async () => {
    const [startDate, endDate] = dateRange;
    
    // If no date range is selected, don't fetch data
    if (!startDate || !endDate) {
      setFollowUpPatients([]);
      return;
    }

    setLoading(true);
    const token = localStorage.getItem("auth_token");
    const tenantId = localStorage.getItem("tenantId");
    const clinicId = localStorage.getItem("clinicId");

    if (!token) {
      setLoading(false);
      return;
    }

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const fromDate = formatDate(startDate);
    const toDate = formatDate(endDate);

    try {
      const response = await axios.get(
        `${API_URL}prescriptions/followup-compact?fromDate=${fromDate}&toDate=${toDate}&clinicId=${clinicId}`,
        // prescriptions/search?fromDate=${fromDate}&toDate=${toDate}&clinicId=${clinicId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            tenantId: tenantId,
          },
        }
      );

      console.log("API Response:", response.data.data); // Debug log

      // FIX: The response has data nested as response.data.data
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // Filter prescriptions that have follow-up dates
        console.log(response.data.data,"dtfgyhujkvghbjnk")
        const followUpData = response.data.data
          .filter(item => item.followUpVisitDate)
          .map(item => {
            // Data is now directly in the item object, not nested in prescription
            
            return {
              id: item.appointmentId,
              patientName: item.patientName || "Unknown Patient",
              doctorName: item.doctorName || "Unknown Doctor", 
              doctorCode: item.userId, // Add doctor code
              patientId: item.patientId, // Add actual patient ID
              followUpDate: item.followUpVisitDate,
              reason: item.consultReason || item.complaints || "Follow-up required",
              status: item.followUpVisitDate >= formatDate(new Date()) ? "Scheduled" : "Pending",
              patientMobileNo: item.patientMobileNo || "N/A",
              appointmentId: item.appointmentId || "N/A",
              prescriptionData: item,
              followUpCompleted: item?.followupdetails?.isfollowup === "Y",
              patientData: null, // No separate patient data in new structure
              doctorData: null, // No separate doctor data in new structure
              visitDate: item.visitDate,
              consultationType: item.consultationType,
              clinicId: item.clinicId,
              prescriptionDoc: item.prescriptionDoc
            };
          });
        
        console.log("Processed follow-up data:", followUpData); // Debug log
        setFollowUpPatients(followUpData);
      } else {
        console.log("No valid data structure found"); // Debug log
        setFollowUpPatients([]);
      }
    } catch (error) {
      console.error("Error fetching follow up patients:", error);
      setFollowUpPatients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFollowUpPatients();
  }, [dateRange]);

  const handleViewDetails = (patient) => {
    setSelectedPatient(patient);
    setIsModalVisible(true);
  };

  const getStatusColor = (status) => {
    return status === "Scheduled" 
      ? "text-[#00BD7F] border-[#00BD7F]" 
      : "text-[#BD8F2C] border-[#BD8F2C]";
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-lg font-semibold text-[#004276]">
            Follow Up Patients{" "}
            <span className="bg-[#00BD7F] text-white font-medium px-2 py-1 rounded-md text-sm ml-1">
              {followUpPatients.length}
            </span>
          </h2>
        </div>
        <div className="flex gap-2 items-center">
          <CalendarDatePicker
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-[0px_4px_8px_rgba(0,0,0,0.15)] flex-1 flex flex-col relative overflow-visible">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 py-8" style={{ height: '360px' }}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00BD7F]"></div>
            <p className="mt-2">Loading follow up patients...</p>
          </div>
        ) : followUpPatients.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 py-8" style={{ height: '360px' }}>
            <img src={nodata} alt="No Data" className="w-48 h-48 mb-4" />
            <div>
              {dateRange[0] && dateRange[1] 
                ? "No Follow Up Patients available for selected date range"
                : "Please select a date range to view follow up patients"
              }
            </div>
          </div>
        ) : (
          <div style={{ height: '360px', overflowY: 'auto' }}>
            {followUpPatients.map((patient) => (
              <div
                key={patient.id}
                className="flex items-center justify-between px-5 py-3 border-b last:border-none"
              >
                <div className="flex items-center gap-3">
                  <div>
                    <p className="font-semibold text-base text-[#1A1A1A] mb-1">
                      {patient.patientName}
                    </p>
                    <p className="text-sm text-[#7D8592] mb-1">
                      Doctor: <span className="font-normal text-[#7D8592]">{patient.doctorName? "Dr.":""} {patient.doctorName}</span> | 
                      Phone: <span className="font-normal text-[#7D8592]">{patient.patientMobileNo}</span>
                    </p>
                    <p className="text-sm text-[#7D8592] mb-0">
                      Follow Up Date: <span className="font-normal text-[#7D8592]">{(() => {
                        const value = patient.followUpDate;
                        if (!value) return "N/A";
                        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
                          const [yyyy, mm, dd] = value.split("-");
                          return `${dd}-${mm}-${yyyy}`;
                        }
                        const dateObj = new Date(value);
                        if (isNaN(dateObj.getTime())) return String(value);
                        const dd = String(dateObj.getDate()).padStart(2, "0");
                        const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
                        const yyyy = dateObj.getFullYear();
                        return `${dd}-${mm}-${yyyy}`;
                      })()}</span> | 
                      Reason: <span className="font-normal text-[#7D8592]">{patient.reason}</span>
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 items-center">
        
                <button
                className={`px-4 py-2 rounded-xl transition-colors ${
                  patient.followUpCompleted
                    ? "bg-[#fff] text-[#00BD7F] border border-[#00BD7F] cursor-not-allowed"
                    : "text-[#004276] border border-[#004276] hover:bg-[#004276] hover:text-white"
                }`}
                disabled={patient.followUpCompleted}
                onClick={() => {
                  if (patient.followUpCompleted) {
                    return;
                  }
                  console.log("Patient data for navigation:", patient);
                  // Navigate to SlotBooking with patient data and follow-up date
                  navigate("/SlotBooking", {
                    state: {
                      patientCode: patient.patientId || patient.appointmentId || patient.id,
                      patient: {
                        firstName: patient.patientName?.split(' ')[0] || '',
                        lastName: patient.patientName?.split(' ').slice(1).join(' ') || '',
                        mobileNumber: patient.patientMobileNo || ''
                      },
                      followUpDate: patient.followUpDate,
                      doctorCode: patient.doctorCode, // Add doctor code
                      isFollowUp: true,
                      refAppointmentId: patient.appointmentId,
                    }
                  });
                }}
              >
                {patient.followUpCompleted ? "Follow Up Completed" : "Schedule Follow Up"}
              </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>


    </div>
  );
}